import { client } from "../api/client.js";
import { renderPreview } from "../ui/previews.js";
import { formatDateTimeMDY12h } from "../utils/date.js";
import { escapeHtml } from "../ui/escapeHtml.js";
import { SOCIAL_TEMPLATES } from "../data/socialTemplates.js";
import { openCampaignWizard } from "../ui/campaignWizard.js";
import { isComingSoon } from "../data/comingSoon.js";
import { PLATFORM_IMAGE_SIZES } from "../data/mediaLimits.js";
import { searchPhotos } from "../utils/unsplash.js";
import { searchStockVideos } from "../utils/pexels.js";
import { validateMediaForPlatform, MEDIA_LIMITS } from "../data/mediaLimits.js";
import { toast } from "../ui/toast.js";
import { renderAiAssistToolbar } from "../ui/aiAssistToolbar.js";
import { renderCharCounter, updateCharCounter, setCharCounterPlatform } from "../ui/charCounter.js";

export function openMediaLibrary(page, initialTab = 'my-media') {
    closeMediaLibrary();

    const tabs = ['my-media', 'stock', 'stock-videos', 'ai-generate', 'recent'];
    const startTab = tabs.includes(initialTab) ? initialTab : 'my-media';

    const overlay = document.createElement('div');
    overlay.className = 'ml-overlay';
    overlay.innerHTML = `
        <div class="ml-modal">
            <div class="ml-modal-header">
                <div>
                    <h3 class="ml-modal-title">Media Library</h3>
                </div>
                <button class="ml-close-btn" id="ml-close">&times;</button>
            </div>
            <div class="ml-tabs">
                <button class="ml-tab ${startTab === 'my-media' ? 'active' : ''}" data-ml-tab="my-media">My Media</button>
                <button class="ml-tab ${startTab === 'stock' ? 'active' : ''}" data-ml-tab="stock">Stock Photos</button>
                <button class="ml-tab ${startTab === 'stock-videos' ? 'active' : ''}" data-ml-tab="stock-videos">Stock Videos</button>
                <button class="ml-tab ${startTab === 'ai-generate' ? 'active' : ''}" data-ml-tab="ai-generate">AI Generate</button>
                <button class="ml-tab ${startTab === 'recent' ? 'active' : ''}" data-ml-tab="recent">Recent</button>
            </div>
            <div class="ml-tab-content" id="ml-tab-content">
                <!-- Content rendered dynamically based on active tab -->
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    const modal = overlay.querySelector('.ml-modal');
    let activeTab = startTab;

    // Close handlers
    overlay.querySelector('#ml-close')?.addEventListener('click', closeMediaLibrary);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeMediaLibrary(); });
    document.addEventListener('keydown', function _mlEsc(e) {
        if (e.key === 'Escape') { closeMediaLibrary(); document.removeEventListener('keydown', _mlEsc); }
    });

    // Select media — use server URL directly, adapt to whichever page opened the modal
    modal.addEventListener('click', (e) => {
        const item = e.target.closest('[data-action="select-media"]');
        if (item && page) {
            const url = item.dataset.url;
            const name = item.dataset.name || 'media';
            const type = item.dataset.type || 'image/jpeg';
            const credit = item.dataset.credit || undefined;
            const isUnsplash = item.dataset.unsplash === 'true';
            const isPexels = item.dataset.pexels === 'true';
            const externalId = item.dataset.externalId;
            const mediaItemId = item.dataset.mediaId;
            if (url) {
                const media = { name, type, dataUrl: url, size: 0, dimensions: null, fromLibrary: true };
                if (isUnsplash) {
                    media.unsplash = true;
                    media.credit = credit;
                }
                if (isPexels) {
                    media.pexels = true;
                    media.credit = credit;
                }

                // Track usage in the workspace_media table so "Recent" surfaces
                // stock picks alongside uploaded files. Fire-and-forget — any
                // failure here must never block the actual selection.
                if ((isUnsplash || isPexels) && externalId) {
                    client.registerStockMedia({
                        source: isUnsplash ? 'unsplash' : 'pexels',
                        external_id: externalId,
                        external_url: url,
                        thumb_url: item.dataset.thumb || url,
                        original_name: name,
                        file_type: type,
                        credit
                    }).catch(() => {});
                } else if (mediaItemId) {
                    client.touchWorkspaceMedia(mediaItemId).catch(() => {});
                }

                // Generic picker: if the caller set an onMediaPick callback,
                // invoke it with the media item and close the modal. Used by
                // single-select pickers (e.g. blog featured image) that don't
                // fit the multi-media attached/drawer model below.
                if (typeof page.onMediaPick === 'function') {
                    try { page.onMediaPick(media); } catch (_) {}
                    closeMediaLibrary();
                    return;
                }

                // Content Studio — validate against the tightest platform
                // the user has selected so we catch X's 4-image/no-mix cap
                // and IG's 10-total cap before the post is rendered wrong.
                if (page.attachedMedia !== undefined) {
                    const selectedPlatforms = Object.entries(page.platforms || {})
                        .filter(([_, on]) => on)
                        .map(([p]) => p);
                    const candidate = [...page.attachedMedia, media];
                    let blocked = null;
                    for (const p of selectedPlatforms) {
                        const result = validateMediaForPlatform(p, candidate);
                        if (!result.ok) { blocked = { platform: p, error: result.error }; break; }
                    }
                    if (blocked) {
                        toast('warn', blocked.error);
                        closeMediaLibrary();
                        return;
                    }
                    page.attachedMedia.push(media);
                    if (page.updatePreviewWithMedia) page.updatePreviewWithMedia();
                }
                // Calendar drawer
                else if (page.drawerMedia !== undefined) {
                    page.drawerMedia.push(media);
                    const list = document.querySelector('#drawer-media-list');
                    if (list && page._renderDrawerMediaList) list.innerHTML = page._renderDrawerMediaList();
                    const previewMount = document.querySelector('#drawer-preview-mount');
                    if (previewMount && page.selectedItem) {
                        const contentArea = document.querySelector('#edit-content');
                        const titleInput = document.querySelector('#drawer-title');
                        const text = contentArea?.value || page.selectedItem.content || '';
                        const title = titleInput?.value || '';
                        previewMount.innerHTML = renderPreview(page.selectedItem.platform, text, title, page.brandName, page.drawerMedia);
                    }
                }
                // Library drawer
                else if (page.draftMedia !== undefined) {
                    page.draftMedia.push(media);
                }
                // Campaigns drawer
                else if (page.editDraft?.media !== undefined) {
                    page.editDraft.media.push(media);
                }

                page.render();
                closeMediaLibrary();
            }
        }
    });

    const isAdmin = ['owner', 'admin'].includes(window.__auth__?.workspace_role);

    function refreshGrid() {
        client.getWorkspaceMedia({ approved_only: isAdmin ? '' : '1' }).then(res => {
            const media = res.media || [];
            window.__mlMedia = media;
            renderTab(activeTab);
        }).catch(() => {
            window.__mlMedia = [];
            renderTab(activeTab);
        });
    }

    function renderTab(tab) {
        const container = document.getElementById('ml-tab-content');
        if (!container) return;
        const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
        const media = window.__mlMedia || [];

        if (tab === 'my-media') {
            container.innerHTML = `
                <div class="ml-modal-upload" id="ml-upload-zone">
                    <input type="file" id="ml-upload-input" accept="image/*,video/*" multiple style="display:none">
                    <label for="ml-upload-input" class="ml-upload-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        Upload or drop files here
                    </label>
                </div>
                <div class="ml-ai-gen" data-testid="ml-ai-gen-section">
                    <details class="ml-ai-gen-details">
                        <summary class="ml-ai-gen-summary" data-testid="ml-ai-gen-toggle">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                            Generate AI image
                        </summary>
                        <div class="ml-ai-gen-body">
                            <input type="text" id="ml-ai-prompt" class="ml-ai-prompt" data-testid="ml-ai-prompt" placeholder="e.g. minimalist desk with espresso cup, warm morning light" maxlength="800" />
                            <div class="ml-ai-gen-row">
                                <select id="ml-ai-size" class="ml-ai-size" data-testid="ml-ai-size">
                                    <option value="1024x1024">Square (1024×1024)</option>
                                    <option value="1792x1024">Landscape (1792×1024)</option>
                                    <option value="1024x1792">Portrait (1024×1792)</option>
                                </select>
                                <button type="button" id="ml-ai-gen-btn" class="btn btn-glass btn-sm" data-testid="ml-ai-gen-btn">Generate</button>
                            </div>
                            <div id="ml-ai-status" class="ml-ai-status" data-testid="ml-ai-status"></div>
                        </div>
                    </details>
                </div>
                <div class="ml-modal-search">
                    <input class="media-library-search" id="ml-search" placeholder="Search by filename...">
                </div>
                <div class="media-library-grid" id="ml-grid">
                    ${media.length === 0 ? '<div class="media-library-empty">No media uploaded yet.</div>' : ''}
                </div>
            `;
            const grid = document.getElementById('ml-grid');
            if (media.length > 0) renderMediaGrid(media, grid, isAdmin);
            bindUploadHandlers(container, refreshGrid);
            bindMyMediaSearch(container, isAdmin);
            bindAiGenHandler(container, refreshGrid);
        } else if (tab === 'stock') {
            container.innerHTML = `
                <div class="ml-modal-search">
                    <input class="media-library-search" id="ml-stock-search" placeholder="Search Unsplash...">
                </div>
                <div class="media-library-grid" id="ml-stock-grid">
                    <div class="media-library-empty">Search for free stock photos</div>
                </div>
                <div class="ml-unsplash-credit">Photos by <a href="https://unsplash.com" target="_blank" rel="noopener">Unsplash</a></div>
            `;
            let stockTimeout;
            container.querySelector('#ml-stock-search')?.addEventListener('input', (e) => {
                clearTimeout(stockTimeout);
                const q = e.target.value.trim();
                if (!q) {
                    const grid = document.getElementById('ml-stock-grid');
                    if (grid) grid.innerHTML = '<div class="media-library-empty">Search for free stock photos</div>';
                    return;
                }
                stockTimeout = setTimeout(async () => {
                    const grid = document.getElementById('ml-stock-grid');
                    if (!grid) return;
                    grid.innerHTML = '<div class="media-library-empty">Searching...</div>';
                    try {
                        const photos = await searchPhotos(q);
                        if (!photos.length) {
                            grid.innerHTML = '<div class="media-library-empty">No photos found.</div>';
                            return;
                        }
                        grid.innerHTML = photos.map(photo => `
                            <div class="media-library-item" data-action="select-media" data-url="${_esc(photo.url)}" data-name="${_esc(photo.alt || 'Unsplash Photo')}" data-type="image/jpeg" data-unsplash="true" data-external-id="${_esc(photo.id)}" data-thumb="${_esc(photo.thumb)}" data-credit="${_esc(photo.author)}">
                                <img src="${_esc(photo.thumb)}" alt="${_esc(photo.alt)}" loading="lazy">
                                <div class="ml-stock-badge">STOCK</div>
                                <div class="media-library-item__info">${_esc(photo.author)}</div>
                            </div>
                        `).join('');
                    } catch (err) {
                        grid.innerHTML = '<div class="media-library-empty">' + _esc(err.message) + '</div>';
                    }
                }, 400);
            });
        } else if (tab === 'stock-videos') {
            container.innerHTML = `
                <div class="ml-modal-search">
                    <input class="media-library-search" id="ml-video-search" placeholder="Search stock videos...">
                </div>
                <div class="media-library-grid" id="ml-video-grid">
                    <div class="media-library-empty">Search for free stock videos</div>
                </div>
                <div class="ml-unsplash-credit">Videos by <a href="https://www.pexels.com" target="_blank" rel="noopener">Pexels</a></div>
            `;
            let videoTimeout;
            container.querySelector('#ml-video-search')?.addEventListener('input', (e) => {
                clearTimeout(videoTimeout);
                const q = e.target.value.trim();
                if (!q) {
                    const grid = document.getElementById('ml-video-grid');
                    if (grid) grid.innerHTML = '<div class="media-library-empty">Search for free stock videos</div>';
                    return;
                }
                videoTimeout = setTimeout(async () => {
                    const grid = document.getElementById('ml-video-grid');
                    if (!grid) return;
                    grid.innerHTML = '<div class="media-library-empty">Searching...</div>';
                    try {
                        const videos = await searchStockVideos(q, 12);
                        if (!videos.length) {
                            grid.innerHTML = '<div class="media-library-empty">No videos found.</div>';
                            return;
                        }
                        grid.innerHTML = videos.map((v) => `
                            <div class="media-library-item ml-video-item">
                                <div class="ml-video-player-wrap">
                                    <video
                                        class="ml-video-player"
                                        src="${_esc(v.downloadUrl)}"
                                        poster="${_esc(v.thumb)}"
                                        preload="metadata"
                                        controls
                                        playsinline></video>
                                    ${v.duration ? `<div class="ml-video-duration">${Math.floor(v.duration / 60)}:${String(v.duration % 60).padStart(2, '0')}</div>` : ''}
                                </div>
                                <div class="ml-video-footer">
                                    <span class="ml-video-author">by ${_esc(v.author)}</span>
                                    <button class="btn btn-sm btn-primary ml-video-select"
                                            data-action="select-media"
                                            data-url="${_esc(v.downloadUrl)}"
                                            data-name="${_esc(`Pexels video · ${v.author}`)}"
                                            data-type="video/mp4"
                                            data-pexels="true"
                                            data-external-id="${_esc(v.id)}"
                                            data-credit="${_esc(v.author)}"
                                            data-thumb="${_esc(v.thumb)}">Use this video</button>
                                </div>
                            </div>
                        `).join('');
                    } catch (err) {
                        const msg = err.code === 'PEXELS_NOT_CONFIGURED'
                            ? 'Stock videos not yet configured. Ask your admin to add PEXELS_API_KEY.'
                            : ('Error: ' + (err.message || 'unknown'));
                        grid.innerHTML = '<div class="media-library-empty">' + _esc(msg) + '</div>';
                    }
                }, 400);
            });
        } else if (tab === 'ai-generate') {
            // Auto-detect current platform for default size
            const currentPlatform = page?.items?.find(i => i.id === page?.selectedItemId)?.platform
              || Object.keys(page?.platforms || {}).find(k => page.platforms[k]) || 'facebook';
            const defaultSize = PLATFORM_IMAGE_SIZES[currentPlatform] || '1024x1024';
            container.innerHTML = `
                <div class="ml-ai-generate">
                    <div class="ml-ai-section">
                        <h4 class="ml-ai-section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                            Generate Image
                        </h4>
                        <div class="ml-ai-prompt-group">
                            <label class="ml-ai-label">Describe the image</label>
                            <textarea id="ml-ai-img-prompt" class="ml-ai-textarea" rows="3" placeholder="A professional flat-lay photo of a laptop, coffee, and notebook on a wooden desk with warm morning light..."></textarea>
                        </div>
                        <div class="ml-ai-options-row">
                            <div class="ml-ai-opt">
                                <label class="ml-ai-label">Size <span style="font-weight:400;color:var(--text-muted)">(auto: ${currentPlatform})</span></label>
                                <select id="ml-ai-img-size" class="ml-ai-select">
                                    <option value="1024x1024" ${defaultSize === '1024x1024' ? 'selected' : ''}>Square (1024x1024)</option>
                                    <option value="1792x1024" ${defaultSize === '1792x1024' ? 'selected' : ''}>Landscape (1792x1024)</option>
                                    <option value="1024x1792" ${defaultSize === '1024x1792' ? 'selected' : ''}>Portrait (1024x1792)</option>
                                </select>
                            </div>
                        </div>
                        <button class="btn btn-primary btn-sm" id="ml-ai-img-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                            Generate Image
                        </button>
                        <div id="ml-ai-img-result" class="ml-ai-result"></div>
                    </div>
                    <div class="ml-ai-divider"></div>
                    <div class="ml-ai-section">
                        <h4 class="ml-ai-section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                            Generate Video
                        </h4>
                        <div class="ml-ai-prompt-group">
                            <label class="ml-ai-label">Describe the video</label>
                            <textarea id="ml-ai-vid-prompt" class="ml-ai-textarea" rows="3" placeholder="A smooth timelapse of clouds moving over a city skyline at sunset..."></textarea>
                        </div>
                        <div class="ml-ai-options-row">
                            <div class="ml-ai-opt">
                                <label class="ml-ai-label">Aspect Ratio</label>
                                <select id="ml-ai-vid-ratio" class="ml-ai-select">
                                    <option value="16:9">Landscape (16:9)</option>
                                    <option value="9:16">Portrait (9:16)</option>
                                    <option value="1:1">Square (1:1)</option>
                                </select>
                            </div>
                        </div>
                        <button class="btn btn-primary btn-sm" id="ml-ai-vid-btn">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                            Generate Video
                        </button>
                        <div id="ml-ai-vid-result" class="ml-ai-result"></div>
                    </div>
                    <p class="ml-ai-hint">Uses your configured AI provider (DALL-E 3, Imagen 3, or Flux for images; Replicate for video). Configure keys in Settings.</p>
                </div>
            `;

            // Image generation handler
            container.querySelector('#ml-ai-img-btn')?.addEventListener('click', async () => {
                const prompt = container.querySelector('#ml-ai-img-prompt')?.value?.trim();
                const size = container.querySelector('#ml-ai-img-size')?.value || '1024x1024';
                const resultDiv = container.querySelector('#ml-ai-img-result');
                const btn = container.querySelector('#ml-ai-img-btn');
                if (!prompt) { resultDiv.innerHTML = '<div class="ml-ai-error">Enter an image description first.</div>'; return; }
                btn.disabled = true; btn.innerHTML = '<span class="ml-ai-spinner"></span> Generating...';
                resultDiv.innerHTML = '<div class="ml-ai-loading">Generating image... this may take 15-30 seconds</div>';
                try {
                    const res = await client.generateAiImage({ prompt, size });
                    if (res.ok && res.media) {
                        resultDiv.innerHTML = `
                            <div class="ml-ai-preview">
                                <img src="${_esc(res.media.url)}" alt="AI Generated" class="ml-ai-preview-img">
                                <div class="ml-ai-preview-actions">
                                    <button class="btn btn-primary btn-sm" data-action="select-media" data-url="${_esc(res.media.url)}" data-name="${_esc(res.media.name)}" data-type="${_esc(res.media.type)}">Use This Image</button>
                                    <button class="btn btn-ghost btn-sm" id="ml-ai-img-regen">Regenerate</button>
                                </div>
                                ${res.media.provider ? '<div class="ml-ai-provider-badge">Generated with ' + _esc(res.media.provider === 'openai' ? 'DALL-E 3' : res.media.provider === 'google' ? 'Imagen 3' : 'Flux') + '</div>' : ''}
                            </div>
                        `;
                        container.querySelector('#ml-ai-img-regen')?.addEventListener('click', () => {
                            container.querySelector('#ml-ai-img-btn')?.click();
                        });
                    } else {
                        resultDiv.innerHTML = '<div class="ml-ai-error">' + _esc(res.error?.message || 'Generation failed') + '</div>';
                    }
                } catch (err) {
                    resultDiv.innerHTML = '<div class="ml-ai-error">' + _esc(err.message || 'Generation failed. Check your API key in Settings.') + '</div>';
                }
                btn.disabled = false; btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> Generate Image';
            });

            // Video generation handler
            container.querySelector('#ml-ai-vid-btn')?.addEventListener('click', async () => {
                const prompt = container.querySelector('#ml-ai-vid-prompt')?.value?.trim();
                const aspectRatio = container.querySelector('#ml-ai-vid-ratio')?.value || '16:9';
                const resultDiv = container.querySelector('#ml-ai-vid-result');
                const btn = container.querySelector('#ml-ai-vid-btn');
                if (!prompt) { resultDiv.innerHTML = '<div class="ml-ai-error">Enter a video description first.</div>'; return; }
                btn.disabled = true; btn.innerHTML = '<span class="ml-ai-spinner"></span> Generating...';
                resultDiv.innerHTML = '<div class="ml-ai-loading">Generating video... this may take 1-5 minutes</div>';
                try {
                    const res = await client.generateAiVideo({ prompt, aspectRatio });
                    if (res.ok && res.media) {
                        resultDiv.innerHTML = `
                            <div class="ml-ai-preview">
                                <video src="${_esc(res.media.url)}" controls class="ml-ai-preview-video" style="max-width:100%;border-radius:8px;"></video>
                                <div class="ml-ai-preview-actions">
                                    <button class="btn btn-primary btn-sm" data-action="select-media" data-url="${_esc(res.media.url)}" data-name="${_esc(res.media.name)}" data-type="video/mp4">Use This Video</button>
                                    <button class="btn btn-ghost btn-sm" id="ml-ai-vid-regen">Regenerate</button>
                                </div>
                            </div>
                        `;
                        container.querySelector('#ml-ai-vid-regen')?.addEventListener('click', () => {
                            container.querySelector('#ml-ai-vid-btn')?.click();
                        });
                    } else {
                        resultDiv.innerHTML = '<div class="ml-ai-error">' + _esc(res.error?.message || 'Video generation failed') + '</div>';
                    }
                } catch (err) {
                    resultDiv.innerHTML = '<div class="ml-ai-error">' + _esc(err.message || 'Video generation failed. Add a Replicate key in Settings.') + '</div>';
                }
                btn.disabled = false; btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> Generate Video';
            });
        } else if (tab === 'recent') {
            // Sort by last_used_at (bumped when a user re-picks an item or
            // selects a stock asset) so Recent reflects true "most recently used"
            // across uploads + Unsplash + Pexels. Falls back to created_at for
            // legacy rows that never got touched.
            const sorted = [...media].sort((a, b) => {
                const da = new Date(a.lastUsedAt || a.uploadedAt || a.createdAt || 0);
                const db = new Date(b.lastUsedAt || b.uploadedAt || b.createdAt || 0);
                return db - da;
            }).slice(0, 20);
            container.innerHTML = `
                <div class="media-library-grid" id="ml-recent-grid">
                    ${sorted.length === 0 ? '<div class="media-library-empty">No recent media.</div>' : ''}
                </div>
            `;
            const grid = document.getElementById('ml-recent-grid');
            if (sorted.length > 0) renderMediaGrid(sorted, grid, isAdmin);
        }
    }

    function bindUploadHandlers(container, onUpload) {
        const uploadInput = container.querySelector('#ml-upload-input');
        const uploadZone = container.querySelector('#ml-upload-zone');
        if (uploadInput) {
            uploadInput.onchange = async (e) => {
                for (const file of e.target.files) {
                    const fd = new FormData();
                    fd.append('file', file);
                    try { await client.uploadWorkspaceMedia(fd); } catch (err) { console.warn('[ML_UPLOAD]', err); }
                }
                uploadInput.value = '';
                onUpload();
            };
        }
        if (uploadZone) {
            uploadZone.addEventListener('dragover', (e) => { e.preventDefault(); uploadZone.classList.add('ml-upload-zone--dragover'); });
            uploadZone.addEventListener('dragleave', () => uploadZone.classList.remove('ml-upload-zone--dragover'));
            uploadZone.addEventListener('drop', async (e) => {
                e.preventDefault();
                uploadZone.classList.remove('ml-upload-zone--dragover');
                for (const file of e.dataTransfer.files) {
                    const fd = new FormData();
                    fd.append('file', file);
                    try { await client.uploadWorkspaceMedia(fd); } catch (err) { console.warn('[ML_UPLOAD]', err); }
                }
                onUpload();
            });
        }
    }

    function bindMyMediaSearch(container, isAdminFlag) {
        container.querySelector('#ml-search')?.addEventListener('input', (e) => {
            const q = e.target.value.toLowerCase();
            const grid = document.getElementById('ml-grid');
            const filtered = (window.__mlMedia || []).filter(m =>
                (m.originalName || m.original_name || '').toLowerCase().includes(q) || (m.tags || []).some(t => t.toLowerCase().includes(q))
            );
            renderMediaGrid(filtered, grid, isAdminFlag);
        });
    }

    // Phase 38 Commit 2 — inline AI image generation inside the media library
    // my-media tab. The Generate button calls /api/generate/image (DALL-E 3
    // via the user's BYOK OpenAI key), then refreshes the grid so the new
    // image lands at the top with last_used_at = now. Failure modes surface
    // inline rather than as toasts so the user doesn't lose context.
    function bindAiGenHandler(container, onGenerated) {
        const btn = container.querySelector('#ml-ai-gen-btn');
        const promptInput = container.querySelector('#ml-ai-prompt');
        const sizeSel = container.querySelector('#ml-ai-size');
        const status = container.querySelector('#ml-ai-status');
        if (!btn || !promptInput) return;
        btn.addEventListener('click', async () => {
            const prompt = (promptInput.value || '').trim();
            if (!prompt) {
                if (status) status.textContent = 'Enter a prompt first.';
                if (status) status.className = 'ml-ai-status ml-ai-status--warn';
                return;
            }
            btn.disabled = true;
            btn.textContent = 'Generating…';
            if (status) {
                status.textContent = 'Calling your AI image provider…';
                status.className = 'ml-ai-status ml-ai-status--info';
            }
            try {
                const res = await client.generateAiImage({
                    prompt,
                    size: sizeSel?.value || '1024x1024',
                });
                if (res?.ok === false) {
                    if (status) {
                        status.textContent = res.error?.message || 'Generation failed.';
                        status.className = 'ml-ai-status ml-ai-status--err';
                    }
                } else {
                    if (status) {
                        status.textContent = 'Done — added to your library.';
                        status.className = 'ml-ai-status ml-ai-status--ok';
                    }
                    promptInput.value = '';
                    if (typeof onGenerated === 'function') onGenerated();
                }
            } catch (err) {
                const body = err?.body || {};
                let msg = body.error?.message || body.message || err.message || 'Generation failed.';
                if (body.code === 'NO_IMAGE_PROVIDER' || body.error?.code === 'NO_IMAGE_PROVIDER') {
                    msg = 'Connect an OpenAI key in Settings → AI Settings to enable image generation.';
                }
                if (status) {
                    status.textContent = msg;
                    status.className = 'ml-ai-status ml-ai-status--err';
                }
            } finally {
                btn.disabled = false;
                btn.textContent = 'Generate';
            }
        });
    }

    // Tab switching
    modal.querySelectorAll('.ml-tab').forEach(btn => {
        btn.addEventListener('click', () => {
            modal.querySelectorAll('.ml-tab').forEach(t => t.classList.remove('active'));
            btn.classList.add('active');
            activeTab = btn.dataset.mlTab;
            renderTab(activeTab);
        });
    });

    refreshGrid();
}


function renderMediaGrid(media, grid, isAdmin) {
    const _esc = s => !s ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    if (!media.length) {
        grid.innerHTML = '<div class="media-library-empty">No matching media.</div>';
        return;
    }
    grid.innerHTML = media.map(m => {
        const isImage = /image/i.test(m.fileType || '');
        const isVideo = /video/i.test(m.fileType || '');
        const pending = !m.approved;
        const source = m.source || 'upload';
        const isStock = source === 'unsplash' || source === 'pexels';
        // For stock rows use the stored thumb for the grid preview, full URL for selection.
        const displaySrc = isStock ? (m.thumbUrl || m.url) : m.url;
        const stockAttrs = isStock
            ? ` data-${source}="true" data-external-id="${_esc(m.externalId || '')}" data-thumb="${_esc(m.thumbUrl || '')}" data-credit="${_esc(m.credit || '')}"`
            : ` data-media-id="${_esc(m.id)}"`;
        const stockBadge = isStock ? `<div class="ml-stock-badge">STOCK</div>` : '';
        return `<div class="media-library-item ${pending ? 'media-library-item--pending' : ''}" data-action="select-media" data-url="${_esc(m.url)}" data-name="${_esc(m.originalName)}" data-type="${_esc(m.fileType || '')}"${stockAttrs}>
            ${isImage ? `<img src="${_esc(displaySrc)}" alt="${_esc(m.originalName)}" loading="lazy">` :
              isVideo ? `<div style="position:relative;width:100%;height:100%;background:#000">
                <video src="${_esc(displaySrc)}#t=0.1" poster="${_esc(m.thumbUrl || '')}" style="width:100%;height:100%;object-fit:cover" muted preload="metadata"></video>
                <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white" style="filter:drop-shadow(0 1px 3px rgba(0,0,0,0.5))"><path d="M8 5v14l11-7z"/></svg>
                </div>
              </div>` :
              `<div style="display:flex;align-items:center;justify-content:center;height:100%;background:var(--surface-2);font-size:24px;">\u{1F4C4}</div>`}
            ${stockBadge}
            <div class="media-library-item__info">${_esc(m.originalName)}${pending ? ' (pending)' : ''}</div>
        </div>`;
    }).join('');
}

export function closeMediaLibrary() {
    document.querySelector('.ml-overlay')?.remove();
    delete window.__mlMedia;
}

export function renderGenerateBar(page) {
    const isChecked = (p) => page.platforms[p] ? 'checked' : '';
    return `
      <div class="gen-bar" data-testid="generate-bar">
        <textarea class="gen-inp" id="gen-bar-topic" data-testid="topic-input" placeholder="What would you like to create? e.g. 'Product launch announcement for our new SaaS tool'" rows="1">${page.topic || ''}</textarea>
        <div class="cmd-chips">
          <div class="chip ${page.platforms.facebook ? 'on' : 'off'}" data-chip-platform="facebook" title="Facebook"><input type="checkbox" name="platform" value="facebook" ${isChecked('facebook')} class="chip-checkbox"><span class="chip-dot" style="background:#1877F2"></span>Facebook</div>
          <div class="chip ${page.platforms.instagram ? 'on' : 'off'}" data-chip-platform="instagram" title="Instagram"><input type="checkbox" name="platform" value="instagram" ${isChecked('instagram')} class="chip-checkbox"><span class="chip-dot" style="background:#E4405F"></span>Instagram</div>
          <div class="chip ${page.platforms.x ? 'on' : 'off'}" data-chip-platform="x" title="X (Twitter)"><input type="checkbox" name="platform" value="x" ${isChecked('x')} class="chip-checkbox"><span class="chip-dot" style="background:#94A3B8"></span>X</div>
          <div class="chip ${page.platforms.linkedin ? 'on' : 'off'}" data-chip-platform="linkedin" title="LinkedIn"><input type="checkbox" name="platform" value="linkedin" ${isChecked('linkedin')} class="chip-checkbox"><span class="chip-dot" style="background:#0A66C2"></span>LinkedIn</div>
          <div class="chip ${page.platforms.tiktok ? 'on' : 'off'}" data-chip-platform="tiktok" title="TikTok (publishing coming soon)"><input type="checkbox" name="platform" value="tiktok" ${isChecked('tiktok')} class="chip-checkbox"><span class="chip-dot" style="background:#EE1D52"></span>TikTok<span class="coming-soon-tag coming-soon-tag--chip">Soon</span></div>
          <div class="chip ${page.platforms.youtube ? 'on' : 'off'}" data-chip-platform="youtube" title="YouTube"><input type="checkbox" name="platform" value="youtube" ${isChecked('youtube')} class="chip-checkbox"><span class="chip-dot" style="background:#FF0000"></span>YouTube</div>
          <div class="chip ${page.platforms.pinterest ? 'on' : 'off'}" data-chip-platform="pinterest" title="Pinterest"><input type="checkbox" name="platform" value="pinterest" ${isChecked('pinterest')} class="chip-checkbox"><span class="chip-dot" style="background:#E60023"></span>Pinterest</div>
          <div class="chip ${page.platforms.reddit ? 'on' : 'off'}" data-chip-platform="reddit" title="Reddit"><input type="checkbox" name="platform" value="reddit" ${isChecked('reddit')} class="chip-checkbox"><span class="chip-dot" style="background:#FF4500"></span>Reddit</div>
        </div>
        <select class="gen-tone-select" id="gen-tone-select" title="Content tone">
          <option value="brand" ${page.tone === 'brand' ? 'selected' : ''}>Brand Voice${page.brandVoice ? ' (' + page.brandVoice.substring(0, 20) + ')' : ''}</option>
          ${['Professional', 'Casual', 'Witty', 'Bold', 'Inspirational', 'Educational', 'Provocative', 'Storytelling', 'Other'].map(t =>
            `<option value="${t}" ${page.tone === t ? 'selected' : ''}>${t === 'Other' ? 'Other...' : t}</option>`
          ).join('')}
        </select>
        <input type="text" class="gen-tone-custom" id="gen-tone-custom" placeholder="e.g. Sarcastic, Gen-Z, Corporate-but-fun..." style="display:${page.tone === 'Other' ? 'block' : 'none'};" value="${page.customTone || ''}" />
        <select class="gen-length-select" id="gen-length-select" title="Post length">
          <option value="short" ${page.postLength === 'short' ? 'selected' : ''}>Short</option>
          <option value="medium" ${page.postLength === 'medium' || !page.postLength ? 'selected' : ''}>Medium</option>
          <option value="long" ${page.postLength === 'long' ? 'selected' : ''}>Long</option>
        </select>
        <label class="gen-ai-media-check" title="Auto-generate an AI image for each post"><input type="checkbox" id="gen-ai-image-check"> AI Image</label>
        <label class="gen-ai-media-check" title="Auto-generate a faceless slideshow video (voiceover + stock images + captions) for each video-eligible platform post. Renders in the background — drafts appear immediately, videos land in Library as each finishes." data-testid="gen-faceless-video-label"><input type="checkbox" id="gen-faceless-video-check" data-testid="gen-faceless-video-check"> Faceless video</label>
        <details class="gen-ai-media-advanced" title="Power-user AI video models (Replicate / Zeroscope). The faceless-video checkbox above is the recommended path for most users.">
          <summary class="gen-ai-media-advanced__summary">Advanced</summary>
          <label class="gen-ai-media-check gen-ai-media-check--muted" title="Auto-generate a single AI video clip (Replicate Zeroscope) for each post — short, generic, no voiceover. For most use cases, prefer the Faceless video option."><input type="checkbox" id="gen-ai-video-check"> Clip (Replicate)</label>
        </details>
        <button class="gen-template-btn" id="gen-template-btn" title="Post template">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
          ${page.socialTemplate !== 'freeform' ? `<span class="gen-template-badge">${SOCIAL_TEMPLATES.find(t => t.key === page.socialTemplate)?.name || ''}</span>` : ''}
        </button>
        <button class="gen-btn" id="gen-bar-btn" ${page.loading ? 'disabled' : ''} data-testid="generate-btn">
          ${page.loading ? '<span class="spinner"></span> Generating...' : '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/></svg> Generate'}
        </button>
      </div>
    ${page.templatePickerOpen ? `
      <div class="template-picker" id="template-picker">
        <div class="template-picker__header">
          <span class="template-picker__title">Content Template</span>
          <button class="template-picker__close" id="template-picker-close">&times;</button>
        </div>
        <div class="template-picker__grid">
          ${SOCIAL_TEMPLATES.map(t => `
            <div class="template-card ${page.socialTemplate === t.key ? 'template-card--active' : ''}" data-template-key="${t.key}">
              <div class="template-card__name">${t.name}</div>
              <div class="template-card__desc">${t.desc}</div>
            </div>
          `).join('')}
        </div>
      </div>
    ` : ''}
    `;
}

export function renderPreviewPanel(page, selectedItem) {
    const platforms = ['facebook', 'instagram', 'x', 'linkedin', 'tiktok', 'youtube', 'pinterest', 'reddit'];
    const activePlatform = page.previewPlatform || selectedItem?.platform || 'facebook';

    return `
      <div class="preview-panel" data-testid="preview-panel">
        <div class="preview-panel-header">
          <div>
            <div class="preview-panel-title">Live Platform Preview</div>
            <div class="preview-panel-sub">Exactly as it will appear to your audience</div>
          </div>
          <div class="preview-panel-actions">
            <div class="campaign-cta-group" style="position:relative">
              <button class="btn-studio-sec" id="campaign-cta-btn" style="font-size:11px;padding:5px 12px">+ Add to Campaign</button>
              <div class="campaign-cta-dropdown" id="campaign-cta-dropdown">
                <div class="campaign-cta-list" id="campaign-cta-list"></div>
                <div style="border-top:1px solid var(--border);margin:4px 0"></div>
                <button class="campaign-cta-new" id="campaign-cta-new">+ Create New Campaign</button>
              </div>
            </div>
          </div>
        </div>
        <div class="preview-platform-tabs" data-testid="preview-platform-tabs">
          ${platforms.map(p => `
            <div class="pplt ${activePlatform === p ? 'active' : ''}" data-preview-platform="${p}" role="tab" tabindex="0">
              ${p === 'x' ? 'X (Twitter)' : p === 'tiktok' ? 'TikTok' : p === 'youtube' ? 'YouTube' : p.charAt(0).toUpperCase() + p.slice(1)}
            </div>
          `).join('')}
        </div>
        <div class="preview-scroll">
          <div data-testid="preview-container">
            ${selectedItem ? renderPreview(
              activePlatform,
              selectedItem.text,
              selectedItem.title,
              window.__auth__?.name || "Your Brand",
              selectedItem.media || (page.attachedMedia && page.attachedMedia.length > 0 ? page.attachedMedia : null)
            ) : '<div style="text-align:center;padding:40px;color:var(--text-muted)">No item selected</div>'}
          </div>
        </div>
      </div>
    `;
}

export function renderConfigDrawer(page) {
    const isChecked = (p) => page.platforms[p] ? 'on' : '';
    return `
      <div class="config-drawer-overlay ${page.configDrawerOpen ? 'open' : ''}" data-testid="config-drawer-overlay"></div>
      <div class="config-drawer ${page.configDrawerOpen ? 'open' : ''}" data-testid="config-drawer">
        <div class="config-drawer-header">
          <span class="config-drawer-title">Configuration</span>
          <button class="config-drawer-close" data-testid="config-drawer-close">&times;</button>
        </div>
        <div class="config-drawer-body">
          <div>
            <label class="studio-form-lbl">Campaign</label>
            <select class="studio-form-inp" id="config-campaign">
              <option>Spring Launch</option>
              <option>Q2 Brand Push</option>
            </select>
          </div>
          <div>
            <label class="studio-form-lbl">Context / Tone</label>
            <textarea class="studio-form-inp" style="min-height:64px" placeholder="Add product details, key message, hashtags..." id="config-context">${page.sessionContext || ''}</textarea>
          </div>
          <div>
            <label class="studio-form-lbl">Platforms</label>
            <div class="plat-toggles">
              <div class="pt ${isChecked('facebook')}" data-platform-toggle="facebook"><span class="pt-dot" style="background:#3B82F6"></span>Facebook</div>
              <div class="pt ${isChecked('instagram')}" data-platform-toggle="instagram"><span class="pt-dot" style="background:#EC4899"></span>Instagram</div>
              <div class="pt ${isChecked('x')}" data-platform-toggle="x"><span class="pt-dot" style="background:#94A3B8"></span>X</div>
              <div class="pt ${isChecked('linkedin')}" data-platform-toggle="linkedin"><span class="pt-dot" style="background:#0A66C2"></span>LinkedIn</div>
              <div class="pt ${isChecked('tiktok')}" data-platform-toggle="tiktok"><span class="pt-dot" style="background:#EE1D52"></span>TikTok<span class="coming-soon-tag coming-soon-tag--chip">Soon</span></div>
              <div class="pt ${isChecked('youtube')}" data-platform-toggle="youtube"><span class="pt-dot" style="background:#FF0000"></span>YouTube</div>
              <div class="pt ${isChecked('pinterest')}" data-platform-toggle="pinterest"><span class="pt-dot" style="background:#E60023"></span>Pinterest</div>
              <div class="pt ${isChecked('reddit')}" data-platform-toggle="reddit"><span class="pt-dot" style="background:#FF4500"></span>Reddit</div>
            </div>
          </div>
          <div>
            <label class="studio-form-lbl">Attach Media</label>
            <div class="media-zone">
              <div class="media-zone-ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg></div>
              <div class="media-zone-lbl">Drop image or video here</div>
              <div class="media-zone-sub">JPG, PNG, GIF, MP4, MOV</div>
            </div>
            ${window.__auth__?.workspace_id ? '<button class="media-library-btn" data-action="open-media-library">Browse Media Library</button>' : ''}
          </div>
          <div style="margin-top:8px; padding-top:12px; border-top:1px dashed var(--border);">
            <label class="studio-form-lbl">Campaign Mode</label>
            <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-2);cursor:pointer">
              <input type="checkbox" id="config-campaign-check" ${page.campaignMode ? 'checked' : ''}> Save as Campaign
              ${page.campaignId ? '<span style="color:var(--success);font-size:11px;font-weight:600">Linked</span>' : ''}
            </label>
            ${page.campaignMode ? `
              <div style="margin-top:8px">
                <input type="text" class="studio-form-inp" id="config-campaign-name" value="${escapeHtml(page.campaignName)}" placeholder="Campaign Name (e.g. Summer Launch)">
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
}

export function renderSocialWorkbench(page, selectedItem) {
    return `
      ${page.mode === 'ai-complete' ? renderGenerateBar(page) : ''}
      <div class="action-strip">
        <button class="strip-btn" id="btn-new-draft" data-testid="btn-new-draft"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> New Draft</button>
        <button class="strip-btn" data-testid="social-drafts-toggle"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> Drafts</button>
        <button class="strip-btn ai-only" data-testid="strip-create-campaign"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg> Create Campaign</button>
        <div class="strip-sep"></div>
        <button class="strip-btn" id="copy-strip-btn"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy</button>
        <button class="strip-btn" data-testid="export-json-btn" id="btn-export-json" ${page.items.length === 0 ? 'disabled' : ''}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Export</button>
        <div class="strip-spacer"></div>
        <button class="strip-btn strip-btn--danger" data-testid="strip-clear-all"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg> Clear All</button>
      </div>
      <div class="studio-layout create-workbench">
        <div class="studio-left">
          <main class="create-editor" data-testid="social-editor-root">
            ${renderEditorCard(page, selectedItem)}
          </main>
        </div>
        <aside data-testid="results-content">
          ${renderPreviewPanel(page, selectedItem)}
          ${page.items.length > 0 ? `
            <section data-testid="items-list">
              ${renderItemsListCard(page)}
            </section>
          ` : ''}
        </aside>
      </div>
    `;
}

export function renderSocialDraftsDrawer(page) {
    const isOpen = page.socialDraftsDrawerOpen;
    return `
      <div class="social-drafts-overlay ${isOpen ? 'open' : ''}" data-testid="social-drafts-overlay"></div>
      <div class="social-drafts-drawer ${isOpen ? 'open' : ''}" data-testid="social-drafts-drawer">
        <div class="drawer-header">
          <h2 class="drawer-title">Drafts</h2>
          <button class="drawer-close-btn" data-testid="social-drafts-close">&times;</button>
        </div>

        <div class="drafts-tabs">
          <button class="drafts-tab ${page.draftFilterTab === 'social' ? 'active' : ''}" data-draft-tab="social">Social</button>
          <button class="drafts-tab ${page.draftFilterTab === 'blog' ? 'active' : ''}" data-draft-tab="blog">WordPress</button>
        </div>

        <div class="drawer-actions">
          <button class="social-btn full-width" data-testid="social-drafts-new">New Draft +</button>
        </div>

        <div class="drawer-search-wrapper">
          <input type="text" class="social-drafts-search" placeholder="Search saved drafts..." value="${page.socialDraftQuery || ''}" data-testid="social-drafts-search" />
        </div>

        <div class="drawer-list" data-testid="social-drafts-list">
          <h3 class="drawer-section-label">${page.draftFilterTab === 'social' ? 'Social' : 'WordPress'} Drafts</h3>
          ${page.draftFilterTab === 'social'
            ? (page.socialDraftsLoading ? '<div class="drawer-loading">Loading...</div>' : renderSocialDraftItems(page))
            : renderBlogDraftItemsInSocialDrawer(page)
          }
        </div>
      </div>
    `;
}

export function renderSocialDraftItems(page) {
    const query = (page.socialDraftQuery || '').toLowerCase();
    const filtered = (page.socialDrafts || []).filter(p => {
      const body = p.text || p.content || '';
      return ((p.title || '').toLowerCase().includes(query)) ||
        (body.toLowerCase().includes(query));
    });

    if (filtered.length === 0) {
      return page.socialDraftsError
        ? `<div class="drawer-empty-state">${page.socialDraftsError}</div>`
        : '<div class="drawer-empty-state">No drafts found.</div>';
    }

    return filtered.map(p => {
      const body = p.text || p.content || '';
      const displayTitle = p.title || page.truncate(body, 40);
      return `
      <div class="social-draft-item" data-db-id="${p.dbId || p.id}" data-testid="social-draft-item">
        <div class="draft-title">${escapeHtml(page.truncate(displayTitle, 40))}</div>
        <div class="draft-meta">
          ${p.updated_at ? new Date(p.updated_at).toLocaleDateString() : ''}
          <span class="platform-indicator platform-indicator--${p.platform}">${p.platform}</span>
          <span class="status-pill status-pill--${p.status}">${p.status}</span>
        </div>
      </div>`;
    }).join('');
}

export function renderPlatformSelector(page) {
    const isChecked = (p) => page.platforms[p] ? 'checked' : '';
    return `
      <div class="create-section">
            <label class="create-label">Target Platforms</label>
            <div class="create-platform-selector">
              <label class="create-checkbox"><input type="checkbox" id="platform-facebook" name="platform" value="facebook" ${isChecked('facebook')}> Facebook</label>
              <label class="create-checkbox"><input type="checkbox" id="platform-instagram" name="platform" value="instagram" ${isChecked('instagram')}> Instagram</label>
              <label class="create-checkbox"><input type="checkbox" id="platform-x" name="platform" value="x" ${isChecked('x')}> X (Twitter)</label>
              <label class="create-checkbox"><input type="checkbox" id="platform-linkedin" name="platform" value="linkedin" ${isChecked('linkedin')}> LinkedIn</label>
              <label class="create-checkbox"><input type="checkbox" id="platform-tiktok" name="platform" value="tiktok" ${isChecked('tiktok')}> TikTok</label>
              <label class="create-checkbox"><input type="checkbox" id="platform-youtube" name="platform" value="youtube" ${isChecked('youtube')}> YouTube</label>
            </div>
        </div>
      `;
}

export function renderConfigCard(page) {
    let bodyContent = '';
    let footerContent = '';

    const linkedText = page.campaignId ? `<span class="create-helper-text" style="color:var(--success); margin-left:8px;">✓ Linked</span>` : '';

    // Shared campaign inputs
    const campaignInputs = `
      <div class="create-section create-campaign-options" style = "margin-top: 16px; padding-top: 16px; border-top: 1px dashed #e2e8f0;" >
        <label class="create-checkbox">
          <input type="checkbox" id="campaign-check" ${page.campaignMode ? 'checked' : ''}>
            Save as Campaign
            ${linkedText}
        </label>
            ${page.campaignMode ? `
                <div style="margin-top: 8px;">
                    <input type="text" id="campaign-name" class="create-input" value="${escapeHtml(page.campaignName)}" placeholder="Campaign Name (e.g. Summer Launch)" data-testid="campaign-name-input">
                </div>
            ` : ''
      }
        </div>
      `;

    if (page.mode === 'scheduler-only') {
      bodyContent = `
      <div class="create-section">
          <label class="create-label">Content</label>
          <textarea id="topic" class="create-textarea" rows="4" placeholder="Write your post content here..." data-testid="topic-input">${page.topic}</textarea>
      </div>
      ${renderPlatformSelector(page)}
        ${campaignInputs}
    <div class="create-section" style="margin-top: 20px;">
      <button class="btn-secondary full-width" id="add-draft-btn">New Draft +</button>
    </div>
    `;
    } else if (page.mode === 'ai-assisted') {
      // AI Assisted — user writes draft, AI optimizes
      bodyContent = `
      <div class="create-section">
          <label class="create-label">Your Draft</label>
          <textarea id="topic" class="create-textarea" rows="5" placeholder="Write your draft content here and let AI optimize it..." data-testid="topic-input">${page.topic}</textarea>
      </div>
      ${campaignInputs}
    `;

      footerContent = `
      <div class="create-card__footer">
        <button class="btn-glass full-width" id="generate-btn" ${page.loading ? 'disabled' : ''} data-testid="generate-btn">
          ${page.loading ? '<span class="spinner"></span> Optimizing...' : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/></svg> AI Optimize'}
        </button>
      </div>
      `;
    } else {
      // AI Complete mode
      bodyContent = `
      <div class="create-section">
          <label class="create-label">Main Topic / Theme <span class="required">*</span></label>
          <textarea id="topic" class="create-textarea" rows="3" placeholder="What to post about?" data-testid="topic-input">${page.topic}</textarea>
      </div>
      ${campaignInputs}
    `;

      footerContent = `
      <div class="create-card__footer">
        <button class="btn-glass full-width" id="generate-btn" ${page.loading ? 'disabled' : ''} data-testid="generate-btn">
          ${page.loading ? '<span class="spinner"></span> Generating...' : 'Generate Batch'}
        </button>
      </div>
      `;
    }

    return `
      <div class="create-card create-card--config" aria-label="Configuration">
        <div class="create-card__header">
          <h2 class="create-card__title">
            ${page.mode === 'scheduler-only' ? 'Manual Post' : page.mode === 'ai-assisted' ? 'AI Assist' : 'Configuration'}
          </h2>
        </div>
        <div class="create-card__body">
          ${page.error ? `<div class="create-error" data-testid="error-banner">${escapeHtml(page.error)}</div>` : ''}
          ${bodyContent}
        </div>
        ${footerContent}
      </div>
      `;
}

export function renderItemsListCard(page) {
    const platformIcons = {
      facebook: '<svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
      instagram: '<svg width="14" height="14" viewBox="0 0 24 24" fill="#E4405F"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>',
      x: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
      linkedin: '<svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
      tiktok: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.75a8.18 8.18 0 0 0 4.76 1.52V6.84a4.83 4.83 0 0 1-1-.15z"/></svg>',
      youtube: '<svg width="14" height="14" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>'
    };

    return `
      <div class="create-card create-card--list" style="border-radius:12px">
        <div class="create-card__header" style="padding:16px 20px;border-bottom:1px solid var(--border-subtle)">
          <h2 class="create-card__title" style="font-size:13px;font-weight:700">Generated Posts</h2>
          <span style="font-size:11px;color:var(--text-muted)">${page.items.length} items</span>
        </div>
        ${page.items.map(item => `
          <div class="item-strip ${page.selectedItemId === item.id ? 'selected' : ''}" data-item-id="${item.id}" data-testid="item-card">
            <div class="item-ic" style="background:${item.platform === 'facebook' ? 'rgba(24,119,242,.14)' : item.platform === 'instagram' ? 'rgba(228,64,95,.14)' : item.platform === 'x' ? 'rgba(148,163,184,.12)' : item.platform === 'tiktok' ? 'rgba(238,29,82,.14)' : item.platform === 'youtube' ? 'rgba(255,0,0,.14)' : 'rgba(10,102,194,.14)'}">
              ${platformIcons[item.platform] || ''}
            </div>
            <div class="item-info">
              <div class="item-ttl create-item-title">${escapeHtml(item.title || (item.platform.charAt(0).toUpperCase() + item.platform.slice(1) + (page.campaignName ? ' - ' + page.truncate(page.campaignName, 20) : '')))}</div>
              <div class="item-exc create-item-preview">${escapeHtml(page.truncate(item.text, 50))}</div>
            </div>
            <button class="regenerate-item-btn" data-item-id="${item.id}" data-testid="regenerate-item" title="Regenerate" style="background:none;border:none;cursor:pointer;padding:4px;flex-shrink:0;color:var(--text-muted)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </button>
            <span class="pp pp-${item.platform === 'facebook' ? 'fb' : item.platform === 'instagram' ? 'ig' : item.platform === 'linkedin' ? 'li' : item.platform === 'tiktok' ? 'tt' : item.platform === 'youtube' ? 'yt' : 'x'}" style="flex-shrink:0"><span class="pp-dot"></span>${item.platform === 'facebook' ? 'FB' : item.platform === 'instagram' ? 'IG' : item.platform === 'linkedin' ? 'LI' : item.platform === 'tiktok' ? 'TT' : item.platform === 'youtube' ? 'YT' : 'X'}</span>
            ${item.brandScore != null ? `<span class="brand-score brand-score--${item.brandScore >= 80 ? 'high' : item.brandScore >= 50 ? 'mid' : 'low'}">${item.brandScore}%</span>` : ''}
          </div>
        `).join('')}
        <div style="padding:12px 16px;display:flex;gap:8px">
          <button class="btn-studio-sec" style="flex:1;font-size:12px;justify-content:center" id="btn-save-all">Save All to Library</button>
          <button class="gen-btn gen-btn--glass" style="flex:1;font-size:12px;justify-content:center" id="btn-schedule-all">Schedule All</button>
        </div>
      </div>
    `;
}

/**
 * Per-post destination options for platforms that need more than content+media.
 * Pinterest needs a board (+ optional link); Reddit needs a subreddit, a post
 * kind (self/link), and (for link posts) a URL. Title + body reuse the editor's
 * Title and Content fields. Values live on selectedItem.meta.platform_options
 * and are persisted into scheduled_posts.platform_options at schedule time.
 */
export function renderPlatformOptionsPanel(page, selectedItem) {
    const platform = selectedItem.platform;
    if (platform !== 'pinterest' && platform !== 'reddit') return '';
    const opts = (selectedItem.meta && selectedItem.meta.platform_options) || {};

    if (platform === 'pinterest') {
        const boards = page.pinterestBoards || [];
        const boardSelect = boards.length > 0
            ? `<select id="editor-pin-board" class="create-input" data-testid="pin-board-select">
                 <option value="">Select a board…</option>
                 ${boards.map(b => `<option value="${escapeHtml(String(b.id))}" ${String(opts.board_id) === String(b.id) ? 'selected' : ''}>${escapeHtml(b.name)}</option>`).join('')}
               </select>`
            : `<div class="pin-board-empty" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
                 <button type="button" class="media-library-btn" data-action="load-pinterest-boards" data-testid="load-pinterest-boards">Load my boards</button>
                 ${opts.board_id ? `<span style="font-size:12px;color:var(--text-muted)">Current: ${escapeHtml(opts.board_name || opts.board_id)}</span>` : '<span style="font-size:12px;color:var(--text-muted)">Connect Pinterest in Settings, then load your boards.</span>'}
               </div>`;
        return `
          <div class="create-field-group platform-options-panel" data-testid="pinterest-options" style="background:rgba(230,0,35,0.05);border:1px solid rgba(230,0,35,0.2);border-radius:10px;padding:12px;margin-top:4px">
            <label class="create-label" style="color:#E60023">Pinterest — Pin destination</label>
            <div style="margin-bottom:8px">
              <span class="create-label" style="font-size:11px">Board <span style="color:#E60023">*</span></span>
              ${boardSelect}
            </div>
            <div>
              <span class="create-label" style="font-size:11px">Destination link (optional)</span>
              <input type="url" id="editor-pin-link" class="create-input" value="${escapeHtml(opts.link || '')}" placeholder="https://your-site.com/page">
            </div>
            <div style="margin-top:8px">
              <span class="create-label" style="font-size:11px">Alt text — SEO &amp; accessibility (optional)</span>
              <input type="text" id="editor-pin-alt" class="create-input" maxlength="500" value="${escapeHtml(opts.alt_text || '')}" placeholder="Describe the image with searchable keywords" data-testid="pin-alt-text">
            </div>
            <p class="create-hint" style="font-size:11px;color:var(--text-muted);margin-top:6px">Title uses the Title field above; the description uses Content. Pinterest indexes the title, description, and alt text for search. A Pin requires an image.</p>
          </div>`;
    }

    // Reddit
    const kind = (opts.kind || 'self');
    return `
      <div class="create-field-group platform-options-panel" data-testid="reddit-options" style="background:rgba(255,69,0,0.05);border:1px solid rgba(255,69,0,0.2);border-radius:10px;padding:12px;margin-top:4px">
        <label class="create-label" style="color:#FF4500">Reddit — Submission</label>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          <div style="flex:1;min-width:160px">
            <span class="create-label" style="font-size:11px">Subreddit <span style="color:#FF4500">*</span></span>
            <input type="text" id="editor-reddit-subreddit" class="create-input" value="${escapeHtml(opts.subreddit || '')}" placeholder="e.g. SideProject" data-testid="reddit-subreddit">
          </div>
          <div style="min-width:120px">
            <span class="create-label" style="font-size:11px">Post type</span>
            <select id="editor-reddit-kind" class="create-input" data-testid="reddit-kind">
              <option value="self" ${kind === 'self' ? 'selected' : ''}>Text</option>
              <option value="link" ${kind === 'link' ? 'selected' : ''}>Link</option>
              <option value="image" ${kind === 'image' ? 'selected' : ''}>Image</option>
            </select>
          </div>
        </div>
        ${kind === 'link' ? `
          <div style="margin-top:8px">
            <span class="create-label" style="font-size:11px">Link URL <span style="color:#FF4500">*</span></span>
            <input type="url" id="editor-reddit-url" class="create-input" value="${escapeHtml(opts.url || '')}" placeholder="https://your-site.com/page" data-testid="reddit-url">
          </div>` : ''}
        ${kind === 'image' ? `
          <p class="create-hint" style="font-size:11px;color:var(--text-muted);margin-top:6px">Posts the first attached image as a direct image link. Attach an image in the media zone below.</p>` : ''}
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">
          <div style="flex:1;min-width:140px">
            <span class="create-label" style="font-size:11px">Flair ID (optional)</span>
            <input type="text" id="editor-reddit-flair-id" class="create-input" value="${escapeHtml(opts.flair_id || '')}" placeholder="Flair template ID" data-testid="reddit-flair-id">
          </div>
          <div style="flex:1;min-width:140px">
            <span class="create-label" style="font-size:11px">Flair text (optional)</span>
            <input type="text" id="editor-reddit-flair-text" class="create-input" maxlength="64" value="${escapeHtml(opts.flair_text || '')}" placeholder="Custom flair text" data-testid="reddit-flair-text">
          </div>
        </div>
        <div style="display:flex;gap:16px;flex-wrap:wrap;margin-top:8px;align-items:center">
          <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer"><input type="checkbox" id="editor-reddit-nsfw" ${opts.nsfw ? 'checked' : ''} data-testid="reddit-nsfw"> NSFW</label>
          <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer"><input type="checkbox" id="editor-reddit-spoiler" ${opts.spoiler ? 'checked' : ''} data-testid="reddit-spoiler"> Spoiler</label>
          <label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer"><input type="checkbox" id="editor-reddit-sendreplies" ${opts.sendreplies === false ? '' : 'checked'} data-testid="reddit-sendreplies"> Send reply notifications</label>
        </div>
        <p class="create-hint" style="font-size:11px;color:var(--text-muted);margin-top:6px">Title uses the Title field above; text posts use Content as the body. Some subreddits require flair — find template IDs in the subreddit's post form. Check the subreddit's rules — many remove promotional posts.</p>
      </div>`;
}

export function renderEditorCard(page, selectedItem) {
    if (!selectedItem) {
      return '<div class="create-card create-card--editor"><div class="create-card__body" style="padding:40px;text-align:center;color:var(--text-muted)">No draft selected</div></div>';
    }
    const { isNew } = selectedItem;
    const pills = Object.keys(page.platforms).map(p => `
      <button class="platform-pill ${selectedItem.platform === p ? 'active' : ''} ${page.platforms[p] ? '' : 'disabled'}"
              data-platform="${p}"
              data-testid="editor-pill-${p}"
              ${page.platforms[p] ? '' : 'disabled'}>
        ${page.getPlatformIcon(p)}
      </button>
    `).join('');

    return `
      <div class="create-card create-card--editor" >
         <div class="create-editor-toolbar">
            <h2 class="create-card__title">${isNew ? 'New Draft' : 'Editor'}</h2>
            ${page.activeCreateMode === 'social' ? `
              <div class="social-editor-status">
                ${page.socialDirty ? '<span class="status-pill warn">Unsaved</span>' : ''}
                ${!page.socialDirty && page.socialScheduledAt ? `<span class="status-pill success">Scheduled ${page.socialScheduledAt}</span>` : ''}
                ${!page.socialDirty && !page.socialScheduledAt && page.socialSavedAt ? `<span class="status-pill success">Saved ${formatDateTimeMDY12h(page.socialSavedAt)}</span>` : ''}
              </div>
              <div class="social-editor-actions">
                <button class="social-drafts-toggle-btn" data-testid="social-drafts-toggle">Drafts</button>

              </div>
            ` : ''}
         </div>
         ${page.socialToast ? `
           <div class="toast-banner ${page.socialToast.kind || 'success'}" data-testid="social-toast">
             <span>${page.socialToast.msg || page.socialToast}</span>
             ${page.socialToast.actions ? `<span class="toast-actions">${page.socialToast.actions.map(a => `<button class="toast-action-btn" data-toast-action="${a.action}">${a.label}</button>`).join('')}</span>` : ''}
             <span class="toast-close" data-testid="toast-close">&times;</span>
           </div>
         ` : ''}

         <div class="create-card__body">
            <div class="create-row">
                <div class="create-field-group">
                    <label class="create-label">Title</label>
                    <input type="text" id="editor-title" class="create-input" value="${escapeHtml(selectedItem.title || '')}" placeholder="Post Title">
                </div>
                <div class="create-field-group">
                    <label class="create-label" for="editor-schedule" style="cursor:pointer">Scheduled At</label>
                    <input type="datetime-local" id="editor-schedule" class="create-input" value="${selectedItem.scheduledAt || page.prefilledScheduleDate || ''}">
                </div>
            </div>

            <div class="create-field-group">
                <div class="create-label-row">
                    <label class="create-label">Content</label>
                </div>
                ${renderAiAssistToolbar({ showFillAi: true, showFillToAll: page.items.length > 1, toolbarId: 'create-studio' })}
                <textarea id="editor-content" class="create-textarea create-textarea--sm" data-testid="content-editor" placeholder="Excited to announce our new product launch! Check it out today.">${selectedItem.text || ''}</textarea>
                ${renderCharCounter({ platform: selectedItem.platform || page.previewPlatform || 'facebook', initialLength: (selectedItem.text || '').length, counterId: 'create-char-counter' })}
            </div>

            ${renderPlatformOptionsPanel(page, selectedItem)}

            <div class="create-field-group">
                <label class="create-label">Platforms <span style="font-weight:400;color:var(--text-muted);font-size:11px">(click multiple to expand)</span></label>
                <div class="create-platform-pills create-platform-pills--mockup">
                    <button class="platform-pill-v3 social-generate-blog" data-testid="btn-generate-blog" id="btn-generate-blog">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z"/></svg>
                      Generate Blog
                    </button>
                    ${['facebook','instagram','x','linkedin','tiktok','youtube','pinterest','reddit'].map(p => {
                      const hasItem = page.items.some(i => i.platform === p);
                      const isSelected = selectedItem.platform === p;
                      const dotColors = { facebook:'#1877F2', instagram:'#E4405F', x:'#94A3B8', linkedin:'#0A66C2', tiktok:'#EE1D52', youtube:'#FF0000', pinterest:'#E60023', reddit:'#FF4500' };
                      const labels = { facebook:'Facebook', instagram:'Instagram', x:'X', linkedin:'LinkedIn', tiktok:'TikTok', youtube:'YouTube', pinterest:'Pinterest', reddit:'Reddit' };
                      return `<button class="platform-pill-v3 ${isSelected ? 'active' : ''} ${hasItem && !isSelected ? 'has-item' : ''}" data-platform="${p}" data-testid="editor-pill-${p}">
                        <span class="ppv3-dot" style="background:${dotColors[p]}"></span>${labels[p]}${isComingSoon(p) ? '<span class="coming-soon-tag coming-soon-tag--chip">Soon</span>' : ''}
                      </button>`;
                    }).join('')}
                </div>
            </div>

            <div class="create-field-group" style="margin-top: 4px;">
                <label class="create-label">${['tiktok', 'youtube', 'youtube_shorts'].includes(selectedItem.platform) ? 'Upload Video' : 'Attach Media'}</label>
                <input type="file" id="editor-media-input" accept="${['tiktok', 'youtube', 'youtube_shorts'].includes(selectedItem.platform) ? 'video/*' : 'image/*,video/*'}" multiple style="display:none" data-testid="editor-media-input">
                <label for="editor-media-input" class="media-zone media-zone--editor" data-testid="editor-media-zone" role="button" tabindex="0">
                  <div class="media-zone-ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${['tiktok', 'youtube', 'youtube_shorts'].includes(selectedItem.platform)
                    ? '<polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>'
                    : '<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>'
                  }</svg></div>
                  <div class="media-zone-lbl">${['tiktok', 'youtube', 'youtube_shorts'].includes(selectedItem.platform) ? 'Drop video here or click to select' : 'Tap to attach image or video'}</div>
                  <div class="media-zone-sub">${['tiktok', 'youtube', 'youtube_shorts'].includes(selectedItem.platform) ? 'MP4, MOV, WebM, AVI' : 'JPG, PNG, GIF, HEIC, MP4, MOV - Max 500MB'}</div>
                </label>
                ${window.__auth__?.workspace_id ? '<button class="media-library-btn" data-action="open-media-library">Browse Media Library</button>' : ''}
                ${page.mode !== 'ai-complete' ? `
                <div class="ai-media-quick-btns">
                  <button class="ai-media-quick-btn" data-action="quick-ai-image" data-platform="${selectedItem.platform}">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    Generate Image
                  </button>
                  <button class="ai-media-quick-btn" data-action="quick-ai-video" data-platform="${selectedItem.platform}">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                    Generate Video
                  </button>
                </div>
                ` : ''}
                ${page.attachedMedia && page.attachedMedia.length > 0 ? `
                <div class="media-attached-list" data-testid="media-attached-list">
                  ${page.attachedMedia.map((m, i) => `
                    <div class="media-attached-item" data-media-index="${i}">
                      <div class="media-attached-thumb">
                        ${m.type.startsWith('image/') ? `<img src="${m.dataUrl}" alt="${m.name}">` : `
                          <div class="media-video-thumb" style="position:relative;background:#000">
                            <video src="${m.dataUrl}#t=0.1" style="width:100%;height:100%;object-fit:cover" muted preload="metadata"></video>
                            <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.5))"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                          </div>
                        `}
                      </div>
                      <div class="media-attached-info">
                        <div class="media-attached-name">${m.name}</div>
                        <div class="media-attached-meta">${page.formatFileSize(m.size)}${m.dimensions ? ' - ' + m.dimensions : ''}</div>
                      </div>
                      <button class="media-attached-remove" data-remove-media="${i}" data-testid="remove-media-${i}">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  `).join('')}
                </div>
                ` : ''}
            </div>

            <div class="meta-section" data-testid="metadata-section">
                <button class="meta-toggle" data-testid="meta-toggle" type="button">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  Metadata
                </button>
                <div class="meta-body" data-testid="meta-body">
                  <div class="meta-grid">
                    <div>
                      <label class="meta-lbl">Alt Text</label>
                      <input type="text" class="meta-inp" id="meta-alt" placeholder="Describe the image..." value="${selectedItem.metadata?.alt || ''}">
                    </div>
                    <div>
                      <label class="meta-lbl">Tags</label>
                      <input type="text" class="meta-inp" id="meta-tags" placeholder="tag1, tag2, tag3" value="${selectedItem.metadata?.tags || ''}">
                    </div>
                    <div>
                      <label class="meta-lbl">UTM Source</label>
                      <input type="text" class="meta-inp" id="meta-utm-source" placeholder="e.g. instagram" value="${selectedItem.metadata?.utmSource || ''}">
                    </div>
                    <div>
                      <label class="meta-lbl">UTM Campaign</label>
                      <input type="text" class="meta-inp" id="meta-utm-campaign" placeholder="e.g. spring-launch" value="${selectedItem.metadata?.utmCampaign || ''}">
                    </div>
                  </div>
                </div>
            </div>

            <div class="create-field-group">
                <label class="create-label">Session Context</label>
                <textarea id="session-context" class="create-textarea create-textarea--xs" data-testid="session-context" placeholder="Optional context for this specific post...">${selectedItem.context || ''}</textarea>
            </div>
         </div>

         <div class="create-card__footer">
            <div class="create-actions-row">
                <div class="create-actions-right">
                    ${page.activeCreateMode === 'social' ? `
                      <button class="btn-glass" id="save-btn">Save Draft</button>
                      <button class="btn-glass" id="schedule-post-btn" ${!selectedItem.scheduledAt ? 'disabled' : ''}>Schedule Post</button>
                    ` : `
                      <button class="btn-secondary" id="save-btn">Save Changes</button>
                    `}
                </div>
            </div>
            <div class="create-feedback-row"><span id="autosave-indicator" class="autosave-indicator"></span><div id="save-feedback" class="create-feedback"></div></div>
         </div>
      </div>
      ${page.activeCreateMode === 'social' && page.socialNextSteps ? `
        <div class="next-steps-strip" data-testid="social-next-steps">
          <span class="next-steps-info">${page.socialNextSteps.kind === 'saved' ? 'Draft saved.' : 'Scheduled.'}</span>
          <span class="next-steps-actions">
            <button data-testid="social-new-draft" class="btn-link">New Draft</button>
            <button data-testid="social-drafts-toggle" class="btn-link">Open Drafts</button>
            <button data-testid="next-view-library" class="btn-link">View in Library</button>
          </span>
        </div>
      ` : ''}
      `;
}

export function renderPreviewCard(page, selectedItem) {
    return `
      <div class="create-card create-card--preview create-preview-card" >
        <div class="create-card__header">
           <h2 class="create-card__title">Preview</h2>
        </div>
        <div class="create-card__body">
           <div class="create-preview-container" data-testid="preview-container">
             ${selectedItem ? renderPreview(
      selectedItem.platform,
      selectedItem.text,
      selectedItem.title,
      window.__auth__?.name || "Your Brand"
    ) : '<div class="create-empty-state"><p>No item selected</p></div>'}
           </div>
        </div>
      </div>
      `;
}

export async function loadSocialDrafts(page) {
    if (page.socialDraftsLoading) return;
    page.socialDraftsLoading = true;
    page.socialDraftsError = null;
    try {
      // Fetch social items (buildQuery in client.js handles array params correctly)
      let data = await client.getContentLibrary({ type: 'social' });

      // Treat explicit failure as error
      if (data && data.ok === false) {
        throw new Error(data.error || 'Failed to load drafts');
      }

      // Parse from any response shape
      let raw = Array.isArray(data) ? data
        : Array.isArray(data?.items) ? data.items
          : Array.isArray(data?.drafts) ? data.drafts
            : Array.isArray(data?.data?.items) ? data.data.items
              : Array.isArray(data?.data?.drafts) ? data.data.drafts
                : [];

      // Fallback: if type:'social' returned nothing, fetch all and filter client-side
      const socialPlatforms = ['facebook', 'instagram', 'x', 'linkedin', 'twitter', 'tiktok', 'youtube'];
      if (raw.length === 0) {
        data = await client.getContentLibrary({});
        if (data && data.ok === false) {
          throw new Error(data.error || 'Failed to load drafts');
        }
        raw = Array.isArray(data) ? data
          : Array.isArray(data?.items) ? data.items
            : Array.isArray(data?.drafts) ? data.drafts
              : Array.isArray(data?.data?.items) ? data.data.items
                : Array.isArray(data?.data?.drafts) ? data.data.drafts
                  : [];
        // Filter to social platforms only
        raw = raw.filter(it => {
          const p = (it.platform || it.provider || it.meta?.platform || '').toLowerCase();
          return socialPlatforms.includes(p);
        });
      }

      page.socialDrafts = raw.map(it => {
        // Restore attached media so the editor/preview show the image when
        // re-opening a saved post. Accepts a parsed array (it.media) OR the
        // raw JSON string (it.media_json) returned by /api/content/library.
        let media = null;
        if (Array.isArray(it.media) && it.media.length > 0) {
          media = it.media;
        } else if (typeof it.media_json === 'string' && it.media_json !== '[]' && it.media_json !== 'null') {
          try {
            const parsed = JSON.parse(it.media_json);
            if (Array.isArray(parsed) && parsed.length > 0) media = parsed;
          } catch {}
        }
        return {
          dbId: it.id || it.dbId,
          id: it.id || it.dbId,
          platform: it.platform || it.provider || it.meta?.platform || 'facebook',
          title: it.title || '',
          text: it.text || it.content || '',
          status: it.status || 'draft',
          scheduledAt: it.scheduled_at || it.scheduledAt || null,
          updated_at: it.updated_at || it.updatedAt || it.created_at || null,
          meta: it.meta || {},
          media
        };
      });
    } catch (err) {
      console.error('[LOAD_SOCIAL_DRAFTS_ERROR]', err);
      page.socialDraftsError = "Failed to load drafts.";
      page.socialDrafts = [];
    } finally {
      page.socialDraftsLoading = false;
    }
}

export function bindSocialInputs(page) {
    const onInput = (sel, field) => {
      const el = page.container.querySelector(sel);
      if (el) el.oninput = (e) => {
        if (field === 'campaignName') { page.campaignName = e.target.value; page.saveState(); }
        else if (field === 'topic') { page.topic = e.target.value; page.saveState(); }
        else if (field === 'sessionContext') { page.sessionContext = e.target.value; page.saveState(); }
        else page.updateLivePreview(field, e.target.value);
      };
    };

    onInput('#editor-title', 'title');
    onInput('#editor-content', 'text');
    onInput('#editor-schedule', 'scheduledAt');
    // Make clicking anywhere on the datetime input open the picker
    const scheduleInput = page.container.querySelector('#editor-schedule');
    if (scheduleInput) {
        scheduleInput.addEventListener('click', () => {
            try { scheduleInput.showPicker(); } catch {}
        });
    }
    onInput('#editor-context', 'context');
    onInput('#session-context', 'context');
    if (page.campaignMode) onInput('#campaign-name', 'campaignName');
    onInput('#topic', 'topic');

    // Per-post platform options (Pinterest board/link, Reddit subreddit/kind/url).
    // These write into selectedItem.meta.platform_options, which the server syncs
    // into scheduled_posts.platform_options for the publish connector.
    const _selItem = () => page.items.find(i => i.id === page.selectedItemId) || page.activeDraft;
    const setOpt = (key, value) => {
        const item = _selItem();
        if (!item) return;
        if (!item.meta) item.meta = {};
        if (!item.meta.platform_options) item.meta.platform_options = {};
        if (value === '' || value == null) delete item.meta.platform_options[key];
        else item.meta.platform_options[key] = value;
        page.socialDirty = true;
        if (page.scheduleAutosave) page.scheduleAutosave();
    };
    const bindOpt = (sel, key) => {
        const el = page.container.querySelector(sel);
        if (el) el.oninput = el.onchange = (e) => setOpt(key, e.target.value);
    };
    bindOpt('#editor-pin-link', 'link');
    bindOpt('#editor-pin-alt', 'alt_text');
    const pinBoard = page.container.querySelector('#editor-pin-board');
    if (pinBoard) pinBoard.onchange = (e) => {
        const b = (page.pinterestBoards || []).find(x => String(x.id) === e.target.value);
        setOpt('board_id', e.target.value);
        setOpt('board_name', b ? b.name : '');
    };
    bindOpt('#editor-reddit-subreddit', 'subreddit');
    bindOpt('#editor-reddit-url', 'url');
    bindOpt('#editor-reddit-flair-id', 'flair_id');
    bindOpt('#editor-reddit-flair-text', 'flair_text');
    const redditKind = page.container.querySelector('#editor-reddit-kind');
    if (redditKind) redditKind.onchange = (e) => { setOpt('kind', e.target.value); page.render(); };
    // Checkbox options: store true when checked; unchecked = default → drop the
    // key (except sendreplies, whose default is true, so unchecked stores false).
    const bindOptCheckbox = (sel, key, defaultOn) => {
        const el = page.container.querySelector(sel);
        if (el) el.onchange = (e) => {
            if (defaultOn) setOpt(key, e.target.checked ? '' : false);
            else setOpt(key, e.target.checked ? true : '');
        };
    };
    bindOptCheckbox('#editor-reddit-nsfw', 'nsfw', false);
    bindOptCheckbox('#editor-reddit-spoiler', 'spoiler', false);
    bindOptCheckbox('#editor-reddit-sendreplies', 'sendreplies', true);

    const pasteInput = page.container.querySelector('#paste-input');
    if (pasteInput) pasteInput.oninput = (e) => page.handleAutoSplit(e.target.value);

    // Media zone: use event delegation on container so it works after re-renders
    if (!page._mediaEventsAttached) {
      page._mediaEventsAttached = true;

      page.container.addEventListener('click', async (e) => {
        // Media zone is now a <label for="editor-media-input"> — no need to call input.click()
        // The label natively triggers the file input on tap/click
        const removeBtn = e.target.closest('[data-remove-media]');
        if (removeBtn) {
          e.stopPropagation();
          const idx = parseInt(removeBtn.dataset.removeMedia);
          page.removeMedia(idx);
        }

        // Media Library drawer
        const mlBtn = e.target.closest('[data-action="open-media-library"]');
        if (mlBtn) {
          e.preventDefault();
          e.stopPropagation();
          openMediaLibrary(page);
        }

        // Load Pinterest boards for the per-post board picker
        const loadBoards = e.target.closest('[data-action="load-pinterest-boards"]');
        if (loadBoards) {
          e.preventDefault();
          e.stopPropagation();
          loadBoards.disabled = true;
          loadBoards.textContent = 'Loading…';
          try {
            const res = await client.listPinterestBoards();
            page.pinterestBoards = (res && res.boards) || [];
            if (!page.pinterestBoards.length) {
              page.socialToast = { msg: 'No Pinterest boards found. Create a board on Pinterest, then reload.', kind: 'warn' };
            }
            page.render();
          } catch (err) {
            page.socialToast = { msg: 'Could not load Pinterest boards — make sure Pinterest is connected in Settings.', kind: 'warn' };
            page.render();
          }
          return;
        }
        // Quick AI image/video generation (AI Assisted / Manual modes)
        const quickAiImg = e.target.closest('[data-action="quick-ai-image"]');
        if (quickAiImg) {
          e.preventDefault(); e.stopPropagation();
          const platform = quickAiImg.dataset.platform || 'facebook';
          const contentText = page.items?.find(i => i.platform === platform)?.text || page.topic || '';
          if (!contentText.trim()) { page.socialToast = { msg: 'Write some content first, then generate an image.', kind: 'warn' }; page.render(); return; }
          const size = PLATFORM_IMAGE_SIZES[platform] || '1024x1024';
          quickAiImg.disabled = true; quickAiImg.innerHTML = '<span class="ml-ai-spinner"></span> Generating...';
          try {
            const res = await client.generateAiImage({ prompt: 'Create a compelling social media image for: ' + contentText.substring(0, 300), size });
            if (res.ok && res.media) {
              if (!page.attachedMedia) page.attachedMedia = [];
              page.attachedMedia.push({ name: res.media.name, type: res.media.type, dataUrl: res.media.url, size: res.media.fileSize || 0, fromLibrary: true });
              page.render();
            } else { page.socialToast = { msg: res.error?.message || 'Image generation failed', kind: 'error' }; page.render(); }
          } catch (err) { page.socialToast = { msg: err.message || 'Image generation failed', kind: 'error' }; page.render(); }
        }
        const quickAiVid = e.target.closest('[data-action="quick-ai-video"]');
        if (quickAiVid) {
          e.preventDefault(); e.stopPropagation();
          const platform = quickAiVid.dataset.platform || 'facebook';
          const contentText = page.items?.find(i => i.platform === platform)?.text || page.topic || '';
          if (!contentText.trim()) { page.socialToast = { msg: 'Write some content first, then generate a video.', kind: 'warn' }; page.render(); return; }
          quickAiVid.disabled = true; quickAiVid.innerHTML = '<span class="ml-ai-spinner"></span> Generating...';
          try {
            const res = await client.generateAiVideo({ prompt: 'Create a short video clip for: ' + contentText.substring(0, 300), aspectRatio: platform === 'tiktok' ? '9:16' : '16:9' });
            if (res.ok && res.media) {
              if (!page.attachedMedia) page.attachedMedia = [];
              page.attachedMedia.push({ name: res.media.name, type: res.media.type, dataUrl: res.media.url, size: res.media.fileSize || 0, fromLibrary: true });
              page.render();
            } else { page.socialToast = { msg: res.error?.message || 'Video generation failed', kind: 'error' }; page.render(); }
          } catch (err) { page.socialToast = { msg: err.message || 'Video generation failed', kind: 'error' }; page.render(); }
        }
        // Note: select-media is handled by the drawer's own click handler (openMediaLibrary)
        // which uses direct URL reference instead of re-downloading the file
        const mlClose = e.target.closest('[data-action="close-media-library"]');
        if (mlClose) closeMediaLibrary();
        const mlBackdrop = e.target.closest('.media-library-backdrop');
        if (mlBackdrop) closeMediaLibrary();
      });

      page.container.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('[data-testid="editor-media-zone"]')) {
          e.preventDefault();
          const input = page.container.querySelector('#editor-media-input');
          if (input) input.click();
        }
      });

      page.container.addEventListener('change', (e) => {
        if (e.target.id === 'editor-media-input' && e.target.files.length > 0) {
          page.handleMediaFiles(e.target.files);
          e.target.value = '';
        }
      });

      page.container.addEventListener('dragover', (e) => {
        const zone = e.target.closest('[data-testid="editor-media-zone"]');
        if (zone) {
          e.preventDefault();
          e.stopPropagation();
          zone.classList.add('media-zone--dragover');
        }
      });

      page.container.addEventListener('dragleave', (e) => {
        const zone = e.target.closest('[data-testid="editor-media-zone"]');
        if (zone) {
          e.preventDefault();
          e.stopPropagation();
          zone.classList.remove('media-zone--dragover');
        }
      });

      page.container.addEventListener('drop', (e) => {
        const zone = e.target.closest('[data-testid="editor-media-zone"]');
        if (zone) {
          e.preventDefault();
          e.stopPropagation();
          zone.classList.remove('media-zone--dragover');
          if (e.dataTransfer.files.length > 0) page.handleMediaFiles(e.dataTransfer.files);
        }
      });
    }

    // Metadata toggle
    const metaToggle = page.container.querySelector('[data-testid="meta-toggle"]');
    const metaBody = page.container.querySelector('[data-testid="meta-body"]');
    if (metaToggle && metaBody) {
      metaToggle.addEventListener('click', () => {
        metaToggle.classList.toggle('open');
        metaBody.classList.toggle('show');
      });
    }

    // Metadata inputs
    const metaFields = { '#meta-alt': 'alt', '#meta-tags': 'tags', '#meta-utm-source': 'utmSource', '#meta-utm-campaign': 'utmCampaign' };
    for (const [sel, key] of Object.entries(metaFields)) {
      const el = page.container.querySelector(sel);
      if (el) {
        el.oninput = (e) => {
          const item = page.items.find(i => i.id === page.selectedItemId);
          if (item) {
            if (!item.metadata) item.metadata = {};
            item.metadata[key] = e.target.value;
            page.socialDirty = true;
            page.scheduleAutosave();
            page.saveState();
          }
        };
      }
    }

    // Campaign CTA dropdown
    const campaignBtn = page.container.querySelector('#campaign-cta-btn');
    const campaignDropdown = page.container.querySelector('#campaign-cta-dropdown');
    if (campaignBtn && campaignDropdown) {
        campaignBtn.onclick = async () => {
            const isOpen = campaignDropdown.classList.toggle('open');
            if (isOpen) {
                const listEl = page.container.querySelector('#campaign-cta-list');
                if (listEl) {
                    listEl.innerHTML = '<div style="padding:8px 12px;font-size:11px;color:var(--text-muted)">Loading...</div>';
                    try {
                        const res = await client.getCampaigns();
                        const campaigns = res.campaigns || res.data || [];
                        if (campaigns.length === 0) {
                            listEl.innerHTML = '<div style="padding:8px 12px;font-size:11px;color:var(--text-muted)">No campaigns yet</div>';
                        } else {
                            listEl.innerHTML = campaigns.slice(0, 8).map(c =>
                                `<button class="campaign-cta-item" data-campaign-id="${c.id}">${escapeHtml(c.name || c.goal || 'Untitled')}</button>`
                            ).join('');
                        }
                    } catch { listEl.innerHTML = '<div style="padding:8px 12px;font-size:11px;color:var(--text-muted)">Failed to load</div>'; }
                }
            }
        };

        // Click existing campaign
        campaignDropdown.addEventListener('click', async (e) => {
            const item = e.target.closest('[data-campaign-id]');
            if (item) {
                const campaignId = item.dataset.campaignId;
                const selected = page.activeDraft || page.items.find(i => i.id === page.selectedItemId);
                if (selected && campaignId) {
                    try {
                        await client.saveToCampaign(campaignId, { items: [{ platform: selected.platform, content: selected.text, title: selected.title }] });
                        page.toast?.('success', 'Added to campaign!') || alert('Added to campaign!');
                    } catch (err) { page.toast?.('error', err.message) || alert('Error: ' + err.message); }
                }
                campaignDropdown.classList.remove('open');
            }
        });

        // Create new campaign
        const newCampaignBtn = page.container.querySelector('#campaign-cta-new');
        if (newCampaignBtn) {
            newCampaignBtn.onclick = () => {
                campaignDropdown.classList.remove('open');
                const selected = page.activeDraft || page.items.find(i => i.id === page.selectedItemId);
                openCampaignWizard({
                    template: {
                        name: selected?.title || page.topic || '',
                        goal: selected?.text?.substring(0, 100) || page.topic || '',
                        context: selected?.text || ''
                    },
                    onComplete: (result) => { page.campaignId = result?.id; page.render(); }
                });
            };
        }

        // Close on outside click
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.campaign-cta-group')) campaignDropdown.classList.remove('open');
        }, { once: false });
    }

    // Expand to multiple platforms buttons
    const expandCopyBtn = page.container.querySelector('#expand-copy-btn');
    const expandAiBtn = page.container.querySelector('#expand-ai-btn');
    if (expandCopyBtn) expandCopyBtn.onclick = () => page.expandToPlatforms('copy');
    if (expandAiBtn) expandAiBtn.onclick = () => page.expandToPlatforms('ai');

    // Per-platform expansion chips
    page.container.querySelectorAll('[data-expand-platform]').forEach(btn => {
        btn.onclick = () => {
            const platform = btn.dataset.expandPlatform;
            const sourceItem = page.activeDraft || page.items.find(i => i.id === page.selectedItemId);
            if (!sourceItem || !sourceItem.text) return;
            if (!page.items.some(i => i.platform === platform)) {
                page.items.push({
                    id: page.generateId(),
                    platform: platform,
                    text: sourceItem.text,
                    title: sourceItem.title || '',
                    status: 'draft',
                    meta: { ...(sourceItem.meta || {}) }
                });
                page.render();
            }
        };
    });
}

function renderBlogDraftItemsInSocialDrawer(page) {
    const posts = page.blogPosts || [];
    if (!posts.length) return '<div class="drawer-empty">No blog drafts yet.</div>';
    return posts.map(p => `
      <div class="drawer-item" data-blog-draft-id="${p.id}">
        <div class="drawer-item-title">${escapeHtml(p.title || 'Untitled')}</div>
        <div class="drawer-item-meta">${p.status || 'draft'}</div>
      </div>
    `).join('');
}
